/**
 * Actualiza los datos de un empleado (excepto correo y UID)
 * @param token JWT de autenticación
 * @param values Datos del formulario de empleado (debe incluir uid y sucursal)
 * @returns Promise<ApiResponse>
 */
export const actualizarEmpleado = (token: string, values: any): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    };
    fetch(
      apiUrl + "/empleado/actualizar",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};


/**
 * Crea un nuevo empleado en el backend
 * @param token JWT de autenticación
 * @param values Datos del formulario de empleado
 * @returns Promise<ApiResponse>
 */
export const crearEmpleado = (token: string, values: EmployeeForm): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...values,
        fecha_nacimiento: values.fecha_nacimiento?.format ? values.fecha_nacimiento.format("YYYY-MM-DD") : values.fecha_nacimiento,
        fecha_ingreso: values.fecha_ingreso?.format ? values.fecha_ingreso.format("YYYY-MM-DD") : values.fecha_ingreso,
      }),
    };
    fetch(
      apiUrl + "/empleado/crearEmpleado",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Obtener detalle de un empleado por uid y sucursal (desde backend)
export const getEmpleadoDetalle = (token: string, uid: string, sucursal: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uid, sucursal })
    };

    fetch(
      apiUrl + "/empleado/detalle",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
// Obtener datos de un empleado por uid (sucursal opcional para verificación)
export const getEmpleadoData = async (sucursal: string, uid: string): Promise<any> => {
  try {
    // Usando la API de Firestore compat (ya importada en firebase.js)
    const firestore = (await import('../../firebase')).firestore;
    const docRef = firestore.collection("empleado").doc(uid);
    const docSnap = await docRef.get();
    if (docSnap.exists) {
      const empleadoData = docSnap.data();
      // Verificar que el empleado pertenezca a la sucursal esperada (si se proporciona)
      if (sucursal && empleadoData?.sucursal !== sucursal) {
        return null;
      }
      return empleadoData;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};
import { ApiResponse, EmployeeForm, StatusUpdateParams } from '../../types';

const apiUrl: string = import.meta.env.VITE_ADMIN_URL;

export const saveUser = (token: string, values: EmployeeForm): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        ...values, 
        fecha_nacimiento: values.fecha_nacimiento.format("YYYY-MM-DD"), 
        fecha_ingreso: values.fecha_ingreso.format("YYYY-MM-DD") 
      }),
    };

    fetch(
      apiUrl + "/empleado/crear",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const getAll = (token: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    };

    fetch(
      apiUrl + "/empleado/obtener-todos",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al obtener empleados: ", err);
        reject(err);
      });
  });
};

export const getSellers = (token: string, myself: boolean, uid: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ myself: myself, uid: uid })
    };

    fetch(
      apiUrl + "/empleado/get-sellers",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al obtener empleados: ", err);
        reject(err);
      });
  });
};

export const getByFilter = (token: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "POST",
  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    };

    fetch(
      apiUrl + "/empleado/obtener",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al obtener empleados: ", err);
        reject(err);
      });
  });
};

export const actualizarEstatus = (token: string, values: StatusUpdateParams): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    const requestOptions: RequestInit = {
      method: "PUT",
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...values })
    };

    fetch(
      apiUrl + "/empleado/actualizar-status",
      requestOptions
    )
      .then((res) => res.json())
      .then((data: ApiResponse) => {
        resolve(data);
      })
      .catch((err) => {
        console.error("Error al actualizar empleado: ", err);
        reject(err);
      });
  });
};

// Servicio para cambiar la contraseña de un empleado
export const cambiarPassword = async (token: string, uid: string, nuevaPassword: string): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${apiUrl}/empleado/cambiar-password`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uid, nuevaPassword }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
  return { success: false, message: 'No se pudo actualizar la contraseña', error: (error instanceof Error ? error.message : String(error)) };
  }
};